import { useRequestBillingTransferFromClientMutation } from "@api/billingTransfer.api";
import { Organization } from "@common/types/organization";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { FC } from "react";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import actionsStyles from "../ClientActions.module.scss";
import { ClientDetailsRow } from "../views/clientViewConfig";
import styles from "./RequestBillingTransferFromClientModal.module.scss";

interface RequestBillingTransferFromClientModalProps {
    client: ClientDetailsRow;
    onClose: () => void;
}

export const RequestBillingTransferFromClientModal: FC<
    RequestBillingTransferFromClientModalProps
> = ({ client, onClose }) => {
    const { toast } = useToaster();
    const activeOrganization = useActiveOrganization() as Organization;
    const { isPending, mutateAsync } =
        useRequestBillingTransferFromClientMutation();

    const handleSubmit = async () => {
        await mutateAsync({
            workspaceId: client.id,
            organizationId: activeOrganization.id,
        });

        const primaryAdmin = client.members?.find(
            (member) => member.userId === client.primaryAdminUserId,
        );

        toast(
            `Request sent to ${primaryAdmin?.preferredName ?? client.name} to transfer billing to your firm`,
        );

        onClose();
    };

    return (
        <>
            <StandardModalHeader className={actionsStyles.modalHeader}>
                Ask client to transfer billing
            </StandardModalHeader>
            <StandardModalBody className={actionsStyles.modalBody}>
                <p className={styles.content}>
                    We'll send your client a request to move billing to your
                    firm.
                </p>

                <footer className={styles.footer}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={onClose}
                        disabled={isPending}
                        size="xl"
                    >
                        Cancel
                    </Button>
                    <ButtonWithLoader
                        type="submit"
                        variant="primary"
                        disabled={isPending}
                        loading={isPending}
                        onClick={handleSubmit}
                        size="xl"
                    >
                        Submit request
                    </ButtonWithLoader>
                </footer>
            </StandardModalBody>
        </>
    );
};
